import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BatteryCharging, Brain, LucideAngularModule } from 'lucide-angular';
import { BessForecastAsset, BessForecastBlock, BessForecastResponse } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { BessOperationsService } from '../services/bess-operations.service';

@Component({
  standalone: true,
  selector: 'app-bess-ai-forecast-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  template: `
    <div class="forecast-shell p-2 p-lg-2">
      <section class="hero-card">
        <div>
          <div class="eyebrow">Bess</div>
          <h1>AI Charge / Discharge Forecast</h1>
          <p>Forecast DAM and RTM charge-discharge windows using the current in-house model trained from recent BESS schedules and SOC behavior.</p>
        </div>
        <div class="hero-icons">
          <span class="hero-icon accent"><lucide-icon [img]="BrainIcon" [size]="24"></lucide-icon></span>
          <span class="hero-icon"><lucide-icon [img]="BatteryChargingIcon" [size]="24"></lucide-icon></span>
        </div>
      </section>

      <section class="control-card">
        <label>
          <span>Forecast Date</span>
          <input class="form-control" type="date" [ngModel]="forecastDate()" (ngModelChange)="forecastDate.set($event || todayIsoDate())" />
        </label>
        <label>
          <span>Product</span>
          <select class="form-select" [ngModel]="selectedProduct()" (ngModelChange)="onProductChange($event || 'DAM')">
            <option value="DAM">DAM</option>
            <option value="RTM">RTM</option>
          </select>
        </label>
        <button class="primary-btn" type="button" (click)="load()">Generate Forecast</button>
      </section>

      <section class="summary-grid">
        <article class="summary-card"><small>Assets</small><strong>{{ selectedAssets().length }}</strong></article>
        <article class="summary-card"><small>Charge Blocks</small><strong>{{ totalChargeBlocks() }}</strong></article>
        <article class="summary-card"><small>Discharge Blocks</small><strong>{{ totalDischargeBlocks() }}</strong></article>
        <article class="summary-card"><small>Product</small><strong>{{ selectedProduct() }}</strong></article>
      </section>

      <section class="table-card">
        <div class="section-head">
          <h2>{{ selectedProduct() }} Forecast</h2>
          <span>{{ response()?.forecast_date || forecastDate() }}</span>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Battery</th>
                <th>Plant Type</th>
                <th>Starting SOC</th>
                <th>Charge Blocks</th>
                <th>Discharge Blocks</th>
                <th>Blockwise Trend</th>
                <th>Next 8 Blocks</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="7" class="empty-state">Generating forecast...</td></tr>
              } @else if (selectedAssets().length === 0) {
                <tr><td colspan="7" class="empty-state">No forecast data available yet.</td></tr>
              } @else {
                @for (asset of selectedAssets(); track asset.battery_configuration_id) {
                  <tr>
                    <td>{{ asset.entry_id }}</td>
                    <td>{{ asset.plant_type }}</td>
                    <td>{{ asset.starting_soc }}%</td>
                    <td>{{ asset.total_charge_blocks }}</td>
                    <td>{{ asset.total_discharge_blocks }}</td>
                    <td>
                      <div class="trend-card">
                        <svg viewBox="0 0 300 110" class="trend-chart" preserveAspectRatio="none">
                          <line x1="18" y1="96" x2="282" y2="96" class="axis-line"></line>
                          <line x1="18" y1="10" x2="18" y2="96" class="axis-line"></line>
                          @for (segment of trendSegments(asset.blocks); track $index) {
                            <line
                              [attr.x1]="segment.x1"
                              [attr.y1]="segment.y1"
                              [attr.x2]="segment.x2"
                              [attr.y2]="segment.y2"
                              class="trend-segment"
                              [class.charge]="segment.action === 'charge'"
                              [class.discharge]="segment.action === 'discharge'"
                              [class.idle]="segment.action === 'idle'"
                            ></line>
                          }
                          @for (point of trendPoints(asset.blocks); track point.block) {
                            <circle
                              [attr.cx]="point.x"
                              [attr.cy]="point.y"
                              r="2.2"
                              class="trend-point"
                              [class.charge]="point.action === 'charge'"
                              [class.discharge]="point.action === 'discharge'"
                              [class.idle]="point.action === 'idle'"
                            ></circle>
                          }
                        </svg>
                        <div class="trend-caption">Charging trends upward blockwise, discharging trends downward blockwise.</div>
                      </div>
                    </td>
                    <td>
                      <div class="block-strip">
                        @for (block of previewBlocks(asset.blocks); track block.block_number) {
                          <span class="block-pill" [class.charge]="block.action === 'charge'" [class.discharge]="block.action === 'discharge'">
                            {{ block.block_number }} {{ block.action }} @ {{ block.market_price }}
                          </span>
                        }
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .forecast-shell { display:grid; gap:1rem; }
    .hero-card,.control-card,.summary-card,.table-card {
      border:1px solid rgba(18,40,72,.08);
      border-radius:1rem;
      background:linear-gradient(180deg,#fff 0%,#f7fafc 100%);
      box-shadow:0 16px 35px rgba(15,23,42,.06);
    }
    .hero-card { display:flex; justify-content:space-between; align-items:center; gap:1rem; padding:1.5rem; }
    .eyebrow { text-transform:uppercase; letter-spacing:.08em; font-size:.72rem; color:#6b7b8c; margin-bottom:.35rem; }
    .hero-card h1,.section-head h2 { margin:0; }
    .hero-card p,.section-head span,.summary-card small,.empty-state { color:#607286; }
    .hero-icons { display:flex; gap:.75rem; }
    .hero-icon { width:4rem; height:4rem; display:grid; place-items:center; border-radius:1rem; background:linear-gradient(135deg,#0f766e,#0f4c81); color:#fff; }
    .hero-icon.accent { background:linear-gradient(135deg,#0f5ccf,#3b82f6); }
    .control-card { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:1rem; padding:1rem; align-items:end; }
    .control-card label { display:grid; gap:.35rem; color:#526172; font-size:.88rem; font-weight:600; }
    .primary-btn { border:0; border-radius:.8rem; padding:.85rem 1rem; background:linear-gradient(135deg,#0f5ccf,#2563eb); color:#fff; font-weight:700; }
    .summary-grid { display:grid; grid-template-columns:repeat(4, minmax(0, 1fr)); gap:1rem; }
    .summary-card { padding:1rem 1.1rem; }
    .summary-card small { display:block; text-transform:uppercase; letter-spacing:.05em; font-size:.74rem; }
    .summary-card strong { font-size:1.65rem; color:#18324d; }
    .table-card { padding:1.25rem; }
    .section-head { display:flex; justify-content:space-between; align-items:center; gap:1rem; margin-bottom:1rem; }
    .table-wrap { overflow:auto; }
    table { width:100%; border-collapse:collapse; }
    th,td { padding:.85rem .75rem; border-bottom:1px solid rgba(18,40,72,.08); text-align:left; white-space:nowrap; vertical-align:top; }
    th { color:#6b7b8c; font-size:.8rem; text-transform:uppercase; letter-spacing:.05em; }
    .trend-card { min-width:320px; }
    .trend-chart { width:320px; height:118px; display:block; }
    .axis-line { stroke:#cbd5e1; stroke-width:1; }
    .trend-segment { stroke-width:2.6; stroke-linecap:round; }
    .trend-segment.charge, .trend-point.charge { stroke:#16a34a; fill:#16a34a; }
    .trend-segment.discharge, .trend-point.discharge { stroke:#dc2626; fill:#dc2626; }
    .trend-segment.idle, .trend-point.idle { stroke:#94a3b8; fill:#94a3b8; }
    .trend-caption { margin-top:.45rem; font-size:.72rem; color:#64748b; }
    .block-strip { display:flex; flex-wrap:wrap; gap:.45rem; min-width:420px; }
    .block-pill { display:inline-flex; padding:.32rem .55rem; border-radius:999px; background:#eef2f7; color:#334155; font-size:.76rem; font-weight:700; }
    .block-pill.charge { background:#dcfce7; color:#166534; }
    .block-pill.discharge { background:#fee2e2; color:#b91c1c; }
    .empty-state { text-align:center; padding:2rem .5rem; }
    @media (max-width: 960px) {
      .hero-card,.section-head { flex-direction:column; align-items:flex-start; }
      .control-card,.summary-grid { grid-template-columns:1fr; }
      .block-strip { min-width:unset; }
    }
  `],
})
export class BessAiForecastPage {
  private readonly operationsService = inject(BessOperationsService);
  private readonly toast = inject(ToastService);

  protected readonly BrainIcon = Brain;
  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly loading = signal(false);
  protected readonly response = signal<BessForecastResponse | null>(null);
  protected readonly forecastDate = signal(this.todayIsoDate());
  protected readonly selectedProduct = signal<'DAM' | 'RTM'>('DAM');
  protected readonly selectedAssets = computed(() => this.response()?.products?.[this.selectedProduct()] || []);
  protected readonly totalChargeBlocks = computed(() => this.selectedAssets().reduce((sum, asset) => sum + asset.total_charge_blocks, 0));
  protected readonly totalDischargeBlocks = computed(() => this.selectedAssets().reduce((sum, asset) => sum + asset.total_discharge_blocks, 0));

  constructor() {
    this.syncForecastDate('DAM');
    this.load();
  }

  protected previewBlocks(blocks: BessForecastBlock[]): BessForecastBlock[] {
    if (blocks.length <= 8) return blocks;

    const chargeBlocks = blocks.filter((block) => block.action === 'charge').slice(0, 4);
    const dischargeBlocks = blocks.filter((block) => block.action === 'discharge').slice(0, 4);
    const prioritized = [...chargeBlocks, ...dischargeBlocks];
    const used = new Set(prioritized.map((block) => block.block_number));

    for (const block of blocks) {
      if (prioritized.length >= 8) break;
      if (used.has(block.block_number)) continue;
      prioritized.push(block);
      used.add(block.block_number);
    }

    return prioritized.sort((left, right) => left.block_number - right.block_number).slice(0, 8);
  }

  protected trendSegments(blocks: BessForecastBlock[]): Array<{ x1: number; y1: number; x2: number; y2: number; action: string }> {
    if (blocks.length < 2) return [];
    const maxSoc = Math.max(...blocks.map((block) => Number(block.forecast_soc) || 0), 1);
    const minSoc = Math.min(...blocks.map((block) => Number(block.forecast_soc) || 0), 0);
    const spread = Math.max(maxSoc - minSoc, 1);
    return blocks.slice(1).map((block, index) => {
      const previous = blocks[index];
      return {
        x1: this.scaleX(index, blocks.length),
        y1: this.scaleY(Number(previous.forecast_soc) || 0, minSoc, spread),
        x2: this.scaleX(index + 1, blocks.length),
        y2: this.scaleY(Number(block.forecast_soc) || 0, minSoc, spread),
        action: block.action || previous.action || 'idle',
      };
    });
  }

  protected trendPoints(blocks: BessForecastBlock[]): Array<{ x: number; y: number; action: string; block: number }> {
    if (!blocks.length) return [];
    const maxSoc = Math.max(...blocks.map((block) => Number(block.forecast_soc) || 0), 1);
    const minSoc = Math.min(...blocks.map((block) => Number(block.forecast_soc) || 0), 0);
    const spread = Math.max(maxSoc - minSoc, 1);
    return blocks.map((block, index) => ({
      x: this.scaleX(index, blocks.length),
      y: this.scaleY(Number(block.forecast_soc) || 0, minSoc, spread),
      action: block.action || 'idle',
      block: block.block_number,
    }));
  }

  protected load(): void {
    this.loading.set(true);
    this.operationsService.listForecasts(this.selectedProduct(), this.forecastDate()).subscribe({
      next: (response) => {
        this.response.set(response);
        this.forecastDate.set(response.forecast_date || this.forecastDate());
        this.loading.set(false);
      },
      error: () => {
        this.response.set(null);
        this.loading.set(false);
        this.toast.show('Failed to load BESS forecast', '', 'danger');
      },
    });
  }

  protected todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  protected onProductChange(value: 'DAM' | 'RTM'): void {
    this.selectedProduct.set(value || 'DAM');
    this.syncForecastDate(value || 'DAM');
    this.load();
  }

  private syncForecastDate(product: 'DAM' | 'RTM'): void {
    const today = new Date();
    if (product === 'DAM') {
      today.setDate(today.getDate() + 1);
    }
    this.forecastDate.set(today.toISOString().slice(0, 10));
  }

  private scaleX(index: number, total: number): number {
    const innerWidth = 264;
    if (total <= 1) return 18;
    return 18 + (index * innerWidth) / (total - 1);
  }

  private scaleY(value: number, minSoc: number, spread: number): number {
    const innerHeight = 84;
    return 96 - (((value - minSoc) / spread) * innerHeight + 10);
  }
}
